<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "primevue/usetoast";
import {
    Competency,
    Dimension,
    DirectInput,
    Participant,
    ParticipantActivityType,
    ParticipantStatus,
    Project,
    Trainer
} from "../../../types/PersologOnlineTypes.ts";
import {
    DirectInputAPI,
    LanguageManagementAPI,
    ProfileAdministrationAPI,
    ProfileContractorAPI
} from "../../../api";
import { getTextDependencies } from "../../../utils/LanguageOverviewUtils.ts";
import { getProductQuantity } from "../../../utils/ProjectUtils.ts";
import { usePreferencesStore } from '../../../stores/preferences';
import { isCompetencyValueInvalid, getDimensionSum, isDimensionSumInvalid, isDimensionValid } from "../../../utils/DirectInputValidation";
import TooltipButton from "../../common/TooltipButton.vue";
import BuyButton from "../../common/BuyButton.vue";

const { t } = useI18n();
const toast = useToast();

const props = defineProps({
    visible: { type: Boolean, required: true },
    participant: { type: Object as () => Participant, required: true },
    project: { type: Object as () => Project, required: true },
    trainer: { type: Object as () => Trainer, required: true },
});

const emit = defineEmits(["update:visible", "saved"]);

const loading = ref(false);
const saving = ref(false);
const structure = ref<DirectInput | null>(null);
const lmTexts = ref<Record<string, string>>({});
const preferencesStore = usePreferencesStore();
const availableSelectedProductQuantity = ref(0);

const isFormValid = computed(() => {
    if (!structure.value) return false;
    return structure.value.dimensions.every(isDimensionValid);
});

const canSaveByStatus = computed(() =>
    props.participant.status === ParticipantStatus.ACTIVATED ||
    props.participant.status === ParticipantStatus.STARTED,
);

const hasAvailableSelectedProduct = computed(() => availableSelectedProductQuantity.value > 0);

const canSaveByAvailability = computed(() => canSaveByStatus.value || hasAvailableSelectedProduct.value);

const showBuyProfilesButton = computed(() => !canSaveByAvailability.value);

function getSumConstraintTooltip(dim: Dimension): string {
    if (dim.sumConstraint === "EXACT") {
        return t("direct-input.validation.sum-exact", { max: dim.max });
    }
    return t("direct-input.validation.sum-max", { max: dim.max });
}

// Ordered list of product number prefixes to try when resolving labels, e.g. ["in101", "in100"]
const labelProductPrefixes = ref<string[]>([]);

const product = computed(() => props.project.defaultProduct ?? props.project.product);

/**
 * Resolves a label from LM texts by trying each product prefix in order.
 * For dimension "d" with prefixes ["in101", "in100"], tries:
 *   in101.dimension.d → in100.dimension.d → fallback to abbreviation
 */
function findLabel(type: "dimension" | "competency", id: string): string | undefined {
    for (const prefix of labelProductPrefixes.value) {
        const val = lmTexts.value[`${prefix}.${type}.${id}`]
            ?? lmTexts.value[`${prefix}.${type}.${id.toLowerCase()}`];
        if (val) return val;
    }
    return undefined;
}

function getDimensionLabel(dim: Dimension): string {
    return findLabel("dimension", dim.name) ?? dim.name;
}

function getCompetencyLabel(comp: Competency): string {
    return findLabel("competency", comp.name) ?? comp.name;
}

async function loadStructure() {
    loading.value = true;
    lmTexts.value = {};
    try {
        const browserLang = preferencesStore.selectedLanguage || props.project.language || "en";
        const projectLang = props.project.language || "en";
        const textProduct = `profile-${props.participant.selectedProduct}`;

        // Get dependency chain: profile-in101 → [profile-in100]
        const deps = await getTextDependencies("STAGING");
        const fallbackProducts = deps.find((d) => d.product === textProduct)?.dependencies ?? [];
        const productsToFetch = [textProduct, ...fallbackProducts];

        // Store ordered suffixes for label resolution (strip "profile-" prefix)
        labelProductPrefixes.value = productsToFetch.map((p) => p.replace(/^profile-/, ""));

        // Fetch structure + all text sources in parallel
        const [raw, ...textResults] = await Promise.all([
            DirectInputAPI.getStructureForProduct(product.value),
            ...productsToFetch.map((p) => LanguageManagementAPI.fetchTexts(p).catch(() => [])),
        ]);

        for (const result of textResults) {
            for (const identifier of result) {
                const match = identifier.texts.find((tx) => tx.language === browserLang)
                    ?? identifier.texts.find((tx) => tx.language === projectLang)
                    ?? identifier.texts.find((tx) => tx.language === 'en')
                    ?? identifier.texts[0];
                if (match) lmTexts.value[identifier.identifier] = match.text;
            }
        }

        // Deep clone so the form can mutate values without touching the original
        structure.value = structuredClone(raw);
    } finally {
        loading.value = false;
    }
}

async function loadAvailableQuantity() {
    if (!props.trainer?.id || !props.participant?.selectedProduct) {
        availableSelectedProductQuantity.value = 0;
        return;
    }
    availableSelectedProductQuantity.value = await getProductQuantity(props.trainer.id, props.participant.selectedProduct);
}

async function handleProductBought(updateNeeded: boolean) {
    if (updateNeeded) {
        await loadAvailableQuantity();
    }
}

async function save() {
    if (!structure.value || !canSaveByAvailability.value) return;
    saving.value = true;
    try {
        const isAlreadyActivated =
            props.participant.status === ParticipantStatus.ACTIVATED ||
            props.participant.status === ParticipantStatus.STARTED;

        if (!isAlreadyActivated) {
            await ProfileAdministrationAPI.activateParticipant(props.participant.id!);
        }

        await DirectInputAPI.saveDirectInputForParticipant(
            props.participant.id!,
            props.trainer?.id,
            structure.value,
        );
        await ProfileContractorAPI.generateDirectInput(
            props.participant.id!,
            structure.value,
            props.project?.language,
        );
        await ProfileAdministrationAPI.logParticipantActivity(
            props.participant.id!,
            ParticipantActivityType.DIRECT_INPUT_SAVED,
        );
        toast.add({ severity: "success", summary: t("direct-input.save.success"), life: 3000 });
        emit("saved");
        emit("update:visible", false);
    } catch {
        toast.add({ severity: "error", summary: t("direct-input.save.error"), life: 5000 });
    } finally {
        saving.value = false;
    }
}

watch(
    () => props.visible,
    (val) => {
        if (val) {
            loadStructure();
            loadAvailableQuantity();
        } else {
            structure.value = null;
            lmTexts.value = {};
            availableSelectedProductQuantity.value = 0;
        }
    },
);
</script>

<template>
    <Dialog
        id="direct-input-dialog"
        :visible="visible"
        @update:visible="$emit('update:visible', $event)"
        :header="`${t('direct-input.dialog.title')} — ${participant.firstName} ${participant.name}`"
        modal
        :style="{ width: '560px' }"
        :draggable="false"
        :block-scroll="true"
    >
        <div v-if="loading" class="flex justify-center p-8">
            <i class="pi pi-spinner pi-spin text-3xl"></i>
        </div>
        <template v-else-if="structure">
            <div v-for="dimension in structure.dimensions" :key="dimension.id" class="mb-4">
                <div class="flex items-center justify-between mb-2">
                    <h4 class="text-lg font-semibold m-0">{{ getDimensionLabel(dimension) }}</h4>
                    <div class="flex items-center gap-2">
                        <span
                            class="text-sm font-medium"
                            :class="isDimensionSumInvalid(dimension) ? 'text-red-500' : 'text-green-600'"
                        >
                            {{ t('direct-input.validation.sum-label') }}: {{ getDimensionSum(dimension) }} / {{ dimension.max }}
                        </span>
                        <TooltipButton
                            :content="getSumConstraintTooltip(dimension)"
                            icon="pi pi-info-circle"
                            severity="secondary"
                            text
                            rounded
                            class="p-0 w-8 h-8"
                        />
                    </div>
                </div>
                <div class="flex flex-col gap-2">
                    <div
                        v-for="competency in dimension.competencies"
                        :key="competency.id"
                        class="flex items-center gap-4"
                    >
                        <label :for="`di-${dimension.id}-${competency.id}-0`" class="w-40 text-sm font-medium">
                            <span v-html="getCompetencyLabel(competency)"></span><br>
                            <span class="text-content-muted text-xs">({{ competency.min }}–{{ competency.max }})</span>
                        </label>
                        <div class="flex gap-4">
                            <InputNumber
                                v-for="(_, idx) in competency.values"
                                :key="idx"
                                :id="idx === 0 ? `di-${dimension.id}-${competency.id}-0` : undefined"
                                v-model="competency.values[idx]"
                                :invalid="isCompetencyValueInvalid(competency, idx)"
                                class="w-28"
                            />
                        </div>
                    </div>
                </div>
                <Divider class="mt-4 mb-1" />
            </div>
        </template>
        <template #footer>
            <Button
                id="direct-input-dialog_btn_cancel"
                :label="t('buttons.cancel')"
                icon="pi pi-times"
                text
                @click="$emit('update:visible', false)"
            />
            <Button
                id="direct-input-dialog_btn_save"
                :label="t('buttons.save')"
                icon="pi pi-check"
                :loading="saving"
                :disabled="loading || !isFormValid || !canSaveByAvailability"
                @click="save"
            />
            <BuyButton
                v-if="showBuyProfilesButton"
                :product="participant.selectedProduct"
                :product-name="participant.selectedProduct"
                :trainer="trainer"
                postfix="_direct-input"
                @update="handleProductBought"
            />
        </template>
    </Dialog>
</template>
